import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import StarIcon from '@mui/icons-material/Star';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import api from '../../services/api';
import { LOCATION_LABELS } from '../../components/events/LocationField';
import type { EventLocationKey } from '../../types/event';

// Definitions (agreed 2026-09-26; the backend's services/trendsReport.ts is the source):
// families = distinct CRM families with a Warrior/Angel app account; charts are grouped by
// EVENT date; attendance = checked-in people / people booked, for events that have happened.

interface Period {
  period: string;
  periodLabel: string;
}

interface RegistrationTrend extends Period {
  totalRegistrations: number;
  totalAttendees: number;
  cancellations: number;
}

interface FamilyGrowth extends Period {
  newFamilies: number;
  cumulativeFamilies: number;
}

interface FamilyActivity extends Period {
  activeFamilies: number;
  newFamilies: number;
  returningFamilies: number;
  warriorFamilies: number;
  angelFamilies: number;
}

interface AttendanceRate extends Period {
  attendees: number;
  checkIns: number;
  noShows: number;
  attendanceRate: number;
}

interface Breakdown {
  events: number;
  pastEvents: number;
  bookings: number;
  attendees: number;
  checkedIn: number;
  noShows: number;
  attendanceRate: number | null;
  capacity: number;
  utilization: number | null;
}

interface EventRow {
  eventId: string;
  title: string;
  date: string;
  locationKey: string | null;
  category: string;
  past: boolean;
  capacity: number;
  bookings: number;
  attendees: number;
  checkedIn: number | null;
  noShows: number | null;
  attendanceRate: number | null;
  utilization: number | null;
  cancellations: number;
  feedbackCount: number;
  averageRating: number | null;
}

interface RatingTrend extends Period {
  totalFeedback: number;
  averageRating: number;
  recommendationRate: number;
}

interface PWAUsage extends Period {
  totalSessions: number;
  uniqueUsers: number;
  newInstalls: number;
}

interface SignupConversion extends Period {
  provisioned: number;
  activated: number;
  conversionRate: number;
}

interface TrendsSummary {
  totalFamilies: number;
  activeFamilies?: number;
  totalRegistrations: number;
  totalAttendees?: number;
  cancellations?: number;
  totalCheckIns: number;
  noShows?: number;
  overallAttendanceRate: number;
  averageRating: number;
  totalFeedback: number;
  totalPWAUsers: number;
  totalPWASessions: number;
  totalProvisioned: number;
  totalActivated: number;
  pendingActivation: number;
  signupConversionRate: number;
}

interface TrendsData {
  dateRange: { start: string; end: string; granularity: 'weekly' | 'monthly' };
  registrationTrends: RegistrationTrend[];
  familyGrowth: FamilyGrowth[];
  familyActivity?: FamilyActivity[];
  signupConversion: SignupConversion[];
  attendanceRates: AttendanceRate[];
  byLocation?: Array<Breakdown & { locationKey: string }>;
  byCategory?: Array<Breakdown & { category: string }>;
  events?: EventRow[];
  ratingTrends: RatingTrend[];
  pwaUsage: PWAUsage[];
  summary: TrendsSummary;
}

const locationLabel = (key: string | null) =>
  (key && LOCATION_LABELS[key as EventLocationKey]) || key || 'Unknown';
const pctText = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${n}%`);
const displayDate = (iso: string) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

const SummaryCard: React.FC<{ icon: React.ReactNode; value: React.ReactNode; label: string; detail?: string }> = ({
  icon,
  value,
  label,
  detail,
}) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ textAlign: 'center' }}>
      {icon}
      <Typography variant="h3" fontWeight={700}>
        {value}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      {detail && (
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const ChartCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="h6" fontWeight={600}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

const BreakdownTable: React.FC<{ rows: Array<Breakdown & { name: string }>; nameHeader: string }> = ({ rows, nameHeader }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{nameHeader}</TableCell>
          <TableCell align="right">Events</TableCell>
          <TableCell align="right">Booked</TableCell>
          <TableCell align="right">Checked in</TableCell>
          <TableCell align="right">No-shows</TableCell>
          <TableCell align="right">Attendance</TableCell>
          <TableCell align="right">Capacity used</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.name}>
            <TableCell>{r.name}</TableCell>
            <TableCell align="right">{r.events}</TableCell>
            <TableCell align="right">{r.attendees}</TableCell>
            <TableCell align="right">{r.checkedIn}</TableCell>
            <TableCell align="right">{r.noShows}</TableCell>
            <TableCell align="right">
              {r.pastEvents > 0 && r.checkedIn === 0 && r.noShows > 0 ? (
                <Chip size="small" color="warning" label="0% — no check-ins recorded" />
              ) : (
                pctText(r.attendanceRate)
              )}
            </TableCell>
            <TableCell align="right">{pctText(r.utilization)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const TrendReportContent: React.FC = () => {
  const [data, setData] = useState<TrendsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [granularity, setGranularity] = useState<'weekly' | 'monthly'>('monthly');
  const [dateRange, setDateRange] = useState<string>('1');
  const [pastOnly, setPastOnly] = useState(true);

  const fetchTrends = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('granularity', granularity);

      // The server includes the whole end day and snaps the start to a whole month/week.
      const end = new Date();
      const start = new Date();
      start.setMonth(start.getMonth() - parseInt(dateRange, 10));

      params.append('startDate', start.toISOString().split('T')[0]);
      params.append('endDate', end.toISOString().split('T')[0]);

      const response = await api.get(`/reports/trends?${params.toString()}`);
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch trends:', err);
    } finally {
      setLoading(false);
    }
  }, [granularity, dateRange]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Paper sx={{ p: 6, textAlign: 'center' }}>
        <TrendingUpIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Unable to load trend data
        </Typography>
      </Paper>
    );
  }

  const s = data.summary;
  const hasData = s.totalRegistrations > 0 || s.totalFamilies > 0;
  const eventRows = (data.events ?? []).filter((e) => !pastOnly || e.past);
  const rangeEnd = new Date(new Date(data.dateRange.end).getTime() - 86400000).toISOString().slice(0, 10);

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Time Period</InputLabel>
          <Select value={dateRange} label="Time Period" onChange={(e) => setDateRange(e.target.value)}>
            <MenuItem value="1">Last 1 month</MenuItem>
            <MenuItem value="3">Last 3 months</MenuItem>
            <MenuItem value="6">Last 6 months</MenuItem>
            <MenuItem value="12">Last 12 months</MenuItem>
            <MenuItem value="24">Last 2 years</MenuItem>
          </Select>
        </FormControl>

        <ToggleButtonGroup value={granularity} exclusive onChange={(_, value) => value && setGranularity(value)} size="small">
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="weekly">Weekly</ToggleButton>
        </ToggleButtonGroup>

        {loading && <CircularProgress size={20} />}
      </Box>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 3 }}>
        {displayDate(data.dateRange.start.slice(0, 10))} to {displayDate(rangeEnd)}, by event date. Attendance counts
        people checked in, for events that have already happened. Families are CRM families with a Warrior or Angel app
        account.
      </Typography>

      {!hasData ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <TrendingUpIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No data available for the selected period
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SummaryCard
                icon={<PeopleIcon sx={{ fontSize: 40, color: '#00b7ed', mb: 1 }} />}
                value={s.totalFamilies}
                label="Families"
                detail={s.activeFamilies !== undefined ? `${s.activeFamilies} attended in this period` : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SummaryCard
                icon={<TrendingUpIcon sx={{ fontSize: 40, color: '#4caf50', mb: 1 }} />}
                value={s.totalRegistrations}
                label="Bookings"
                detail={
                  s.totalAttendees !== undefined
                    ? `${s.totalAttendees} people · ${s.cancellations ?? 0} cancelled`
                    : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SummaryCard
                icon={<EventAvailableIcon sx={{ fontSize: 40, color: '#ff9800', mb: 1 }} />}
                value={`${s.overallAttendanceRate}%`}
                label="Attendance"
                detail={`${s.totalCheckIns} checked in${s.noShows !== undefined ? ` · ${s.noShows} no-shows` : ''}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SummaryCard
                icon={<StarIcon sx={{ fontSize: 40, color: '#ffc107', mb: 1 }} />}
                value={s.totalFeedback > 0 ? `${s.averageRating.toFixed(1)} / 5` : '—'}
                label="Average rating"
                detail={`${s.totalFeedback} reviews`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SummaryCard
                icon={<PhoneIphoneIcon sx={{ fontSize: 40, color: '#9c27b0', mb: 1 }} />}
                value={s.totalPWAUsers}
                label="App (PWA) users"
                detail={`${s.totalPWASessions} sessions`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <SummaryCard
                icon={<HowToRegIcon sx={{ fontSize: 40, color: '#2196f3', mb: 1 }} />}
                value={`${s.signupConversionRate}%`}
                label="Sign-up conversion"
                detail={`${s.totalActivated} of ${s.totalProvisioned} family accounts logged in`}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {/* Bookings */}
            <Grid item xs={12} lg={6}>
              <ChartCard title="Bookings" subtitle="Confirmed bookings and the people on them, by event date">
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.registrationTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="periodLabel" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="totalRegistrations" stroke="#00b7ed" name="Bookings" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="totalAttendees" stroke="#4caf50" name="People booked" strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="cancellations" stroke="#f44336" name="Cancelled bookings" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </ChartCard>
            </Grid>

            {/* Attendance */}
            <Grid item xs={12} lg={6}>
              <ChartCard title="Attendance" subtitle="People booked at events that have happened, and who was checked in">
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.attendanceRates}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="periodLabel" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value, name, item) => (name === 'Checked in' ? [`${value} (${item?.payload?.attendanceRate ?? 0}%)`, name] : [value, name])} />
                      <Legend />
                      <Bar dataKey="checkIns" stackId="a" fill="#4caf50" name="Checked in" />
                      <Bar dataKey="noShows" stackId="a" fill="#f44336" name="No-shows" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </ChartCard>
            </Grid>

            {/* Families attending: new vs returning */}
            {data.familyActivity && (
              <Grid item xs={12} lg={6}>
                <ChartCard
                  title="Families attending"
                  subtitle="Families with someone checked in. New = their first event ever; Returning = attended before"
                >
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.familyActivity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="periodLabel" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="newFamilies" stackId="f" fill="#00b7ed" name="New" />
                        <Bar dataKey="returningFamilies" stackId="f" fill="#1565c0" name="Returning" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </ChartCard>
              </Grid>
            )}

            {/* Warrior vs Angel families */}
            {data.familyActivity && (
              <Grid item xs={12} lg={6}>
                <ChartCard title="Warrior and Angel families attending" subtitle="Families with someone checked in, by family type">
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.familyActivity}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="periodLabel" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="warriorFamilies" stackId="t" fill="#ff9800" name="Warrior families" />
                        <Bar dataKey="angelFamilies" stackId="t" fill="#9c27b0" name="Angel families" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </ChartCard>
              </Grid>
            )}

            {/* By location */}
            {data.byLocation && (
              <Grid item xs={12} lg={6}>
                <ChartCard title="By location" subtitle="Events in the period; attendance for those that have happened">
                  <BreakdownTable nameHeader="Location" rows={data.byLocation.map((r) => ({ ...r, name: locationLabel(r.locationKey) }))} />
                </ChartCard>
              </Grid>
            )}

            {/* By category */}
            {data.byCategory && (
              <Grid item xs={12} lg={6}>
                <ChartCard title="By category" subtitle="Events in the period; attendance for those that have happened">
                  <BreakdownTable nameHeader="Category" rows={data.byCategory.map((r) => ({ ...r, name: r.category }))} />
                </ChartCard>
              </Grid>
            )}

            {/* Family Growth */}
            <Grid item xs={12} lg={6}>
              <ChartCard title="Families with app access" subtitle="Dated by the family's first app account (bulk provisioning shows as a jump)">
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.familyGrowth}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="periodLabel" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="cumulativeFamilies" stroke="#00b7ed" fill="#00b7ed" fillOpacity={0.3} name="Total families" strokeWidth={2} />
                      <Area type="monotone" dataKey="newFamilies" stroke="#4caf50" fill="#4caf50" fillOpacity={0.5} name="New families" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </ChartCard>
            </Grid>

            {/* Signup Conversion */}
            <Grid item xs={12} lg={6}>
              <ChartCard title="Sign-up conversion" subtitle="Family accounts created, and first logins, in each period">
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.signupConversion}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="periodLabel" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="provisioned" fill="#90caf9" name="Accounts created (links sent)" />
                      <Bar dataKey="activated" fill="#2196f3" name="First login" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </ChartCard>
            </Grid>

            {/* PWA Usage */}
            <Grid item xs={12} lg={6}>
              <ChartCard title="App (PWA) usage" subtitle="By session date">
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.pwaUsage}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="periodLabel" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="uniqueUsers" stroke="#9c27b0" fill="#9c27b0" fillOpacity={0.3} name="Active users" strokeWidth={2} />
                      <Area type="monotone" dataKey="newInstalls" stroke="#e91e63" fill="#e91e63" fillOpacity={0.5} name="New installs" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </ChartCard>
            </Grid>

            {/* Rating Trends */}
            <Grid item xs={12} lg={6}>
              <ChartCard title="Ratings" subtitle="Feedback on events in each period">
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.ratingTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="periodLabel" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} domain={[0, 5]} label={{ value: 'Rating', angle: -90, position: 'insideLeft', fontSize: 12 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} domain={[0, 100]} label={{ value: 'Recommend %', angle: 90, position: 'insideRight', fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="averageRating" stroke="#ffc107" name="Average rating" strokeWidth={2} dot={{ r: 3 }} />
                      <Line yAxisId="right" type="monotone" dataKey="recommendationRate" stroke="#4caf50" name="Would recommend %" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </ChartCard>
            </Grid>

            {/* Per-event table */}
            {data.events && (
              <Grid item xs={12}>
                <ChartCard title="Events" subtitle="Newest first. Attendance is shown once an event has happened">
                  <FormControlLabel
                    control={<Switch checked={pastOnly} onChange={(e) => setPastOnly(e.target.checked)} size="small" />}
                    label="Past events only"
                  />
                  <TableContainer sx={{ maxHeight: 520 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Event</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell align="right">Booked</TableCell>
                          <TableCell align="right">Checked in</TableCell>
                          <TableCell align="right">No-shows</TableCell>
                          <TableCell align="right">Attendance</TableCell>
                          <TableCell align="right">Capacity used</TableCell>
                          <TableCell align="right">Cancelled</TableCell>
                          <TableCell align="right">Feedback</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {eventRows.map((e) => (
                          <TableRow key={e.eventId} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{displayDate(e.date)}</TableCell>
                            <TableCell>{e.title}</TableCell>
                            <TableCell>{locationLabel(e.locationKey)}</TableCell>
                            <TableCell>{e.category}</TableCell>
                            <TableCell align="right">{e.attendees}</TableCell>
                            <TableCell align="right">{e.checkedIn ?? '—'}</TableCell>
                            <TableCell align="right">{e.noShows ?? '—'}</TableCell>
                            <TableCell align="right">{e.past ? pctText(e.attendanceRate) : 'Upcoming'}</TableCell>
                            <TableCell align="right">{pctText(e.utilization)}</TableCell>
                            <TableCell align="right">{e.cancellations}</TableCell>
                            <TableCell align="right">
                              {e.feedbackCount > 0 ? `${e.averageRating?.toFixed(1)} (${e.feedbackCount})` : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {eventRows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={11} align="center">
                              <Typography color="text.secondary">No events in this period</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </ChartCard>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default TrendReportContent;
